// MovieAddForm.js - Extracted movie form logic from App.js
import { useForm } from '../contexts/FormContext';
import { useModal } from '../contexts/ModalContext';
import { useMediaDataContext } from '../contexts/MediaDataContext';
import { useCommunity } from '../contexts/CommunityContext';
import { useNotification } from '../contexts/NotificationContext';

export const useMovieAddForm = (
  supabase,
  currentUser,
  userProfile,
  setUploadingImage,
  setNewMasterItem,
  newMasterItem,
  newCollectionItem,
  loadUserCollection
) => {
  const form = useForm();
  const modal = useModal();
  const vhsData = useMediaDataContext();
  const community = useCommunity();
  const { showError, showSuccess } = useNotification();

  // Extract year from release date (e.g., "30 Apr 1999" -> "1999")
  const extractYear = releasedDate => {
    if (!releasedDate) return new Date().getFullYear();
    const match = releasedDate.match(/\d{4}/);
    return match ? parseInt(match[0]) : new Date().getFullYear();
  };

  const handleMovieSearchSelect = movieData => {
    // Fill in the MOVIE form with the selected movie data
    setNewMasterItem({
      title: movieData.Title || movieData.title || '',
      year: (movieData.Year || movieData.year || '2023').toString(),
      director: movieData.Director || movieData.director || '',
      actors: movieData.Actors || movieData.actors || '',
      genre: movieData.Genre || movieData.genre || '',
      runtime: movieData.Runtime || movieData.runtime || '',
      description: movieData.Plot || movieData.description || '',
      imdb_rating: movieData.imdbRating || movieData.imdb_rating || '',
      country: movieData.Country || movieData.country || '',
      language: movieData.Language || movieData.language || '',
      rated: movieData.Rated || movieData.rated || '',
      released: movieData.Released || movieData.released || '',
      writer: movieData.Writer || movieData.writer || '',
      producer: movieData.producer || '', // Now from TMDB credits!
      distributor: movieData.distributor || '',
      boxoffice: movieData.boxoffice || movieData.BoxOffice || '', // Handle both cases

      // NEW TMDB FIELDS
      tmdb_id: movieData.tmdb_id || null,
      production_companies: movieData.production_companies || [],
      budget: movieData.budget || null,
      trailer_url: movieData.trailer_url || '',
      tagline: movieData.tagline || '',
      alternate_title: movieData.alternate_title || '',
      backdrop_url: movieData.backdrop_url || null,
      type: movieData.Type || movieData.type || 'movie',
      imdb_id: movieData.imdbID || movieData.imdb_id || '',

      // TV-specific fields
      network: movieData.network || '',
      number_of_seasons: movieData.number_of_seasons || null,
      number_of_episodes: movieData.number_of_episodes || null,
      show_status: movieData.show_status || '',
      last_air_date: movieData.last_air_date || '',
    });

    // Mark data as coming from search to make fields read-only
    form.setIsDataFromSearch(true);

    // Fill in the RELEASE form with some defaults
    form.setReleaseForm({
      ...form.releaseForm,
      release_title: movieData.Title || movieData.title || '', // Auto-populate from movie title
      studio: movieData.studio || '', // Studio can be different for release
      release_year: '',
    });

    // Clear any selected images since we're starting fresh
    form.setSelectedMasterImages([]);
    form.setSelectedReleaseImages(null);

    // Store the poster URL for download during save
    if (movieData.poster_url) {
      window.pendingPosterDownload = {
        url: movieData.poster_url,
        imdbID: movieData.imdbID || 'unknown',
      };
    }

    modal.closeModal('movieSearch');
    // Don't clear form.bulkImportMovieTitle here - let AdminBulkImport handle it
  };

  const handleUPCScanComplete = movieData => {
    modal.closeModal('upcScanner');

    if (movieData.success) {
      // Fill in the MOVIE form with the scanned movie data
      setNewMasterItem({
        title: movieData.movie.title || '',
        year: movieData.movie.year?.toString() || '',
        director: movieData.movie.director || '',
        actors: movieData.movie.actors || '',
        genre: movieData.movie.genre || '',
        studio: '', // Keep empty for movie level
        runtime: movieData.movie.runtime || '',
        description: movieData.movie.description || '',
        imdb_rating: movieData.movie.imdbRating || '',
        country: movieData.movie.country || '',
        language: movieData.movie.language || '',
        rated: movieData.movie.rated || '',
        released: movieData.movie.released || '',
        writer: movieData.movie.writer || '',
        producer: movieData.movie.producer || '',
        distributor: movieData.movie.distributor || '',
        boxoffice: movieData.movie.boxoffice || '',
        // Additional TMDB fields from enhanced edge function
        imdb_id: movieData.movie.imdb_id || '',
        type: movieData.movie.type || '',
        // UPC fields now all go to release instead of movie
        // NEW TMDB Priority fields from UPC scan
        tmdb_id: movieData.movie.tmdb_id || null,
        production_companies: movieData.movie.production_companies || [],
        budget: movieData.movie.budget || null,
        trailer_url: movieData.movie.trailer_url || '',
        tagline: movieData.movie.tagline || '',
        alternate_title: movieData.movie.alternate_title || '',
        backdrop_url: movieData.movie.backdrop_url || null,
      });

      // Mark data as coming from search to make fields read-only (same as manual search)
      form.setIsDataFromSearch(true);

      // Fill in the RELEASE form with some defaults and UPC
      form.setReleaseForm({
        ...form.releaseForm,
        release_title: movieData.movie.title || '', // Auto-populate from movie title
        studio: '', // Keep studio field empty
        release_year: movieData.movie.year || '',
        upc: movieData.movie.upc || '', // UPC goes on release, not movie
        upc_distributor: movieData.movie.upc_distributor || '', // UPC distributor goes on release too
        upc_category: movieData.movie.upc_category || '', // UPC category goes on release too
      });

      // Clear any selected images since we're starting fresh
      form.setSelectedMasterImages([]);
      form.setSelectedReleaseImages([]);

      // Store the poster URL for download during save
      if (movieData.movie.poster_url) {
        window.pendingPosterDownload = {
          url: movieData.movie.poster_url,
          upc: movieData.movie.upc || 'unknown',
        };
      }

      // FIXED: Show the main add movie form instead of the simplified one
      modal.openAddMasterModal(form.editingMasterItem, form.isDataFromSearch);

      // Remove the old simplified modal states
      // setScannedMovieData(movieData.movie);  ← Don't use this anymore
      // setShowScannedMovieForm(true);         ← Don't use this anymore
    } else {
      showError('Failed to lookup movie: ' + (movieData.error || 'Unknown error'));
    }
  };

  const handleSaveMovieAndRelease = async (shouldCreateRelease = true) => {
    if (!newMasterItem.title) {
      showError('Movie title is required');
      return;
    }

    // Extract year from released date for validation
    const movieYear = extractYear(newMasterItem.released);

    if (!movieYear || !newMasterItem.released) {
      showError('Movie release date is required');
      return;
    }

    try {
      setUploadingImage(true);

      // Ensure newMasterItem has the year field extracted from released date
      const masterItemWithYear = {
        ...newMasterItem,
        year: movieYear.toString(), // Convert to string for validation schema
      };

      setNewMasterItem(masterItemWithYear);

      // Step 1: Create/Update the master movie
      const movieResult = await vhsData.handleAddMasterItem(
        null, // uploadImageToStorage not needed for multi-image
        form.editingMasterItem ? form.selectedMasterImages : [], // Only pass images when editing
        null, // imageError - handled by MultiImageUpload
        setUploadingImage,
        form.editingMasterItem,
        false, // Don't add to collection yet - we'll do that after release
        userProfile,
        community.setActivityFeed,
        community.activityFeed,
        masterItemWithYear // Pass the corrected data with extracted year
      );

      if (!movieResult?.success) {
        const errorMsg = movieResult?.error || movieResult?.message || 'Failed to save movie';
        throw new Error(errorMsg);
      }

      const movieId = movieResult.data.id;

      // Step 2: Create the first release (only for new movies and if requested)
      let releaseResult = null; // Declare in broader scope for collection logic
      if (!form.editingMasterItem && shouldCreateRelease) {
        let releaseCoverUrl = null;
        let releaseImageData = [];

        // Process and upload ALL release images if provided
        if (form.selectedReleaseImages && form.selectedReleaseImages.length > 0) {
          try {
            // Upload all release images
            const uploadPromises = form.selectedReleaseImages.map(async (image, index) => {
              if (!image.file) return image; // Skip if no file (existing images)

              // Generate clean filename: movie-title-year-release-timestamp-index.jpg
              const sanitizedTitle = masterItemWithYear.title
                .replace(/[^a-zA-Z0-9]/g, '-')
                .toLowerCase();
              const fileName = `${sanitizedTitle}-${masterItemWithYear.year}-release-${Date.now()}-${index}`;
              const fileExt = image.file.name.split('.').pop();
              const filePath = `release-covers/${fileName}.${fileExt}`;

              const { error } = await supabase.storage
                .from('media-images')
                .upload(filePath, image.file, {
                  cacheControl: '3600',
                  upsert: true,
                });

              if (error) throw error;

              const { data: publicData } = supabase.storage
                .from('media-images')
                .getPublicUrl(filePath);

              return {
                url: publicData.publicUrl,
                caption: image.caption || `Release Image ${index + 1}`,
                isPrimary: image.isPrimary || false,
                order: image.order || index + 1,
              };
            });

            const uploadedReleaseImages = await Promise.all(uploadPromises);

            // Set primary cover URL for backward compatibility
            const primaryReleaseImage =
              uploadedReleaseImages.find(img => img.isPrimary) || uploadedReleaseImages[0];
            if (primaryReleaseImage?.url) {
              releaseCoverUrl = primaryReleaseImage.url;
            }

            // Prepare image data for database
            releaseImageData = uploadedReleaseImages.map((image, index) => ({
              image_url: image.url,
              caption: image.caption,
              is_primary: image.isPrimary,
              image_order: image.order,
              uploaded_by: currentUser.id,
            }));
          } catch (uploadError) {
            console.error('❌ Release image upload failed:', uploadError);
            // Continue without release images
          }
        }

        // Create the release
        const releaseData = {
          master_movie_id: movieId,
          main_title: masterItemWithYear.title, // Add movie title for database reference
          release_title: form.releaseForm.release_title || 'Standard Release',
          studio: form.releaseForm.studio || null,
          release_year: parseInt(form.releaseForm.release_year) || null,
          case_type: form.releaseForm.case_type,
          format: form.releaseForm.format,
          release_country: form.releaseForm.release_country || null,
          region: form.releaseForm.region || null,
          speed: form.releaseForm.speed || null,
          edition: form.releaseForm.edition || null,
          edition_other: form.releaseForm.edition_other || null,
          front_cover_url: releaseCoverUrl || movieResult.data.cover_image_url || null, // Fall back to movie's cover
          release_notes: form.releaseForm.release_notes || null,
          upc: form.releaseForm.upc || null,
          upc_distributor: form.releaseForm.upc_distributor || null,
          upc_category: form.releaseForm.upc_category || null,
          distributor_code: form.releaseForm.distributor_code || null,
          added_by: currentUser.id,
        };

        const { data: releaseInsertResult, error: releaseError } = await supabase
          .from('movie_releases')
          .insert([releaseData])
          .select()
          .single();

        if (releaseError) {
          console.error('❌ Error creating release:', releaseError);
          throw releaseError;
        }

        releaseResult = releaseInsertResult;

        // Save all release images to the release_images table
        if (releaseImageData.length > 0) {
          const releaseImagesWithId = releaseImageData.map(img => ({
            ...img,
            release_id: releaseResult.id,
          }));

          const { error: imagesError } = await supabase
            .from('release_images')
            .insert(releaseImagesWithId);

          if (imagesError) {
            console.error('❌ Error saving release images:', imagesError);
          }
        }
      }

      // Step 3: Add to collection if requested (moved outside release creation block)
      if (form.addToMyCollection && currentUser) {
        try {
          const collectionData = {
            user_id: currentUser.id,
            master_item_id: movieId,
            personal_notes: newCollectionItem.personal_notes || null,
          };

          // Add release_id and format depending on whether a release was created
          if (releaseResult) {
            collectionData.release_id = releaseResult.id;
            collectionData.format = form.releaseFormData?.format || null;
          } else {
            collectionData.format = newCollectionItem.format || null;
          }

          const { error: collectionError } = await supabase
            .from('user_collection_items')
            .insert(collectionData);

          if (collectionError && collectionError.code !== '23505') {
            console.error('Failed to add to collection:', collectionError);
          } else {
            if (userProfile && community.setActivityFeed && community.activityFeed) {
              const actionText = releaseResult
                ? 'added movie and release to collection'
                : 'added movie to collection';
              const itemText = releaseResult
                ? `${movieResult.data.title} (${movieResult.data.year}) - ${releaseResult.release_title}`
                : `${movieResult.data.title} (${movieResult.data.year})`;

              const newActivity = {
                id: Date.now(),
                user: userProfile.username,
                action: actionText,
                item: itemText,
                time: 'Just now',
                type: 'collection',
              };
              community.addActivity(newActivity);
            }

            await loadUserCollection();
          }
        } catch (collectionErr) {
          console.error('Error adding to collection:', collectionErr);
          // Don't fail the whole operation if collection add fails
        }
      }

      // Step 4: Add to wantlist if requested
      if (form.addToMyWantlist && currentUser) {
        await vhsData.addMovieToWantlist(movieId);
      }

      // Success!
      showSuccess(
        form.editingMasterItem
          ? 'Movie updated successfully!'
          : releaseResult
            ? 'Movie and release added successfully!'
            : 'Movie added successfully!'
      );

      // Close modal and reset
      modal.closeModal('addMaster');
      form.setEditingMasterItem(null);
      form.setSelectedMasterImages([]);
      form.setSelectedReleaseImages([]);

      // Call the reset functions that are still in App.js
      if (window.resetFormsWithImages) window.resetFormsWithImages();
      if (window.resetReleaseForm) window.resetReleaseForm();

      form.setAddToMyCollection(true);
      form.setAddToMyWantlist(false);

      // Call bulk import callback if it exists (for bulk operations)
      const result = { success: true, movieData: movieResult.data };
      if (window.bulkImportCallback) {
        window.bulkImportCallback(newMasterItem.title, true, null);
      }

      // Return the updated movie data for potential use by callers
      return result;
    } catch (error) {
      console.error('❌ Error saving movie and release:', error);
      showError('Error saving movie: ' + error.message);

      // Call bulk import callback with error information
      if (window.bulkImportCallback) {
        window.bulkImportCallback(newMasterItem.title, false, error.message);
      }

      // Return error information for bulk import handling
      return {
        success: false,
        error: error.message,
        movieTitle: newMasterItem.title,
      };
    } finally {
      setUploadingImage(false);
    }
  };

  return {
    handleMovieSearchSelect,
    handleUPCScanComplete,
    handleSaveMovieAndRelease,
  };
};
